import { useEffect } from 'react';
import { useLoginStatus } from '@cb/apricot-react';
import { logUserOut } from './utils/user';
import { initialAppState } from './context/initialState';
import { parseJWTToken, useAuth } from './utils/auth';
import { useLocation } from 'react-router-dom';
import { useStateDispatch, useStateValue } from './context/AppContext';
import ErrorPage from './components/nonDigital/error/ErrorPage';
import Login from './components/common/login/Login';
import Signup from './components/common/signup/Signup';
import Spinner from './components/ui/loading/SpinnerWrapper';
import { useKeepUserSessionAlive } from './hooks/useKeepSessionAlive';
import { useAnalytics } from './utils/analytics';
import { LOGIN_STATUS_DELAY_MS } from './constants/delay';
import { ApolloApp } from './ApolloApp';

// Stable Error so ErrorPage's useEffect(error) only fires once per mount.
const CATAPULT_LOGIN_FAILED = new Error('catapult: AWS login failed');

function safeParseJWTToken() {
  try {
    return parseJWTToken();
    // eslint-disable-next-line
  } catch (e) {
    return null;
  }
}

/**
 * this handles routing between components based on the login or error status (determined by the window CB object, a util from Apricot)
 */
function GateKeeper() {
  const loginStatus = useAuth();
  const { isLoggedIn } = useLoginStatus();
  const location = useLocation();

  // Global App state.
  const { globalError } = useStateValue();
  const dispatch = useStateDispatch();

  const CLICK_INTERVAL_IN_MINUTES = 2; // ensure event every 2 minutes
  const SESSION_TIMEOUT_IN_HOURS = 4; // max session for 4 hours

  useAnalytics();
  useKeepUserSessionAlive(CLICK_INTERVAL_IN_MINUTES * 60, SESSION_TIMEOUT_IN_HOURS * 60 * 60);

  useEffect(() => {
    // Prevent token flow check from interfering with the signup flow
    if (location.pathname === '/signup') {
      return;
    }

    // Setting delay to allow loginStatus to update based on cookie presence and avoid false logouts
    const timer = setTimeout(() => {
      if (isLoggedIn) {
        // This can check if the user has logged in but has an invalid token
        const token = safeParseJWTToken();
        if (token === null) {
          logUserOut(false);
        }
      }
    }, LOGIN_STATUS_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isLoggedIn, location.pathname]);

  useEffect(() => {
    if (loginStatus.loggedIn) {
      dispatch({
        user: {
          token: safeParseJWTToken(),
        },
      });
    }
  }, [loginStatus.loggedIn, dispatch]);

  useEffect(() => {
    if (loginStatus.loggedOut) {
      dispatch({ ...initialAppState });
      // Clear out any saved info. Not everything, since we don't want to clear potential magic link session.
      sessionStorage.removeItem('orgEvent');
      sessionStorage.removeItem('studentDynamicColumns');
      sessionStorage.removeItem('studentFilters');
      sessionStorage.removeItem('studentPageSize');
      sessionStorage.removeItem('studentSearch');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('wasWelcomed');
    }
  }, [loginStatus.loggedOut, dispatch]);

  // signup flow will deal with logged in status separately
  if (location.pathname === '/signup') {
    return (
      <div className='container' id='main-content'>
        <Signup />
      </div>
    );
  }

  if (loginStatus.loading) {
    return <Spinner />;
  }

  // catapult failed
  if (loginStatus.error) {
    return <ErrorPage error={CATAPULT_LOGIN_FAILED} source='catapult' />;
  }

  if (loginStatus.loggedOut) {
    return (
      <div className='container' id='main-content'>
        <Login />
      </div>
    );
  }

  if (globalError) {
    return (
      <div className='container' id='main-content'>
        {globalError}
      </div>
    );
  }

  if (loginStatus.loggedIn) {
    return <ApolloApp />;
  }

  return null;
}

export default GateKeeper;
